/* A small enhancement around Dawn's native variant and cart handling. */
if (!customElements.get('mercy-purchase-help')) {
  customElements.define('mercy-purchase-help', class extends HTMLElement {
    connectedCallback() {
      this.product = this.closest('product-info');
      if (!this.product) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('[data-size-open]');
      this.trigger?.addEventListener('click', () => this.dialog.showModal(), { signal });
      this.dialog?.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, { signal });
      this.dialog?.addEventListener('close', () => this.trigger.focus(), { signal });
      // Capture runs before Dawn reads the chosen option IDs. One visible finish
      // maps to the existing Style + Frame options without a second request.
      this.product.addEventListener('change', event => {
        const input = event.target;
        if (input.dataset.optionName !== 'Frame' || input.type !== 'radio') return;
        const style = input.value === 'None' ? 'Frameless' : 'Framed';
        const paired = Array.from(this.product.querySelectorAll('input[data-option-name="Style"]')).find(node => node.value === style);
        if (paired) paired.checked = true;
      }, { capture: true, signal });
      this.product.addEventListener('click', event => {
        const link = event.target.closest('.ml-edition-link');
        if (!link) return;
        const detail = document.getElementById(link.hash.slice(1));
        if (detail?.tagName === 'DETAILS') detail.open = true;
      }, { signal });
      this.product.classList.add('ml-pdp-enhanced');
      this.bar = this.querySelector('[data-purchase-bar]');
      this.barButton = this.querySelector('[data-purchase-action]');
      this.barButton?.addEventListener('click', () => {
        const submit = this.product.querySelector('.product-form__submit');
        if (submit && !submit.disabled && submit.getAttribute('aria-disabled') !== 'true') {
          submit.click();
          // Keep native validation and cart errors visible if an add fails.
          this.product.querySelector('product-form')?.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
      }, { signal });
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.product, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
      this.visibility = new IntersectionObserver(entries => {
        const entry = entries[entries.length - 1];
        // Only once the shopper has scrolled past the main button: the first screen
        // stays clean while they read, and the bar steps in when the CTA leaves view.
        this.pastBuyButton = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.sync();
      }, { threshold: 0 });
      this.sync();
    }
    setText(node, text) { if (node && node.textContent !== text) node.textContent = text; }
    sync() {
      const submit = this.product.querySelector('.product-form__submit');
      // Dawn can re-render the form on variant change; follow the live button.
      if (submit !== this.observedSubmit) {
        if (this.observedSubmit) this.visibility.unobserve(this.observedSubmit);
        if (submit) this.visibility.observe(submit);
        this.observedSubmit = submit;
      }
      const picker = this.product.querySelector('variant-selects');
      let selectedSku = picker?.dataset.selectedSku || '';
      try {
        selectedSku = JSON.parse(picker?.querySelector('[data-selected-variant]')?.textContent || 'null')?.sku || selectedSku;
      } catch { /* Keep the previous selection while Dawn replaces its markup. */ }
      const sku = selectedSku.split('-');
      const size = (sku[2] || '').replace(/^0/, '').replace('x', ' × ');
      const code = sku[3] || '';
      const finish = ({ UF: (window.mlI18n || {}).framelessCanvas || 'Frameless canvas', NAT: (window.mlI18n || {}).naturalFrame || 'Natural frame', BLK: (window.mlI18n || {}).blackFrame || 'Black frame' })[code] || '';
      const priceBox = this.product.querySelector('[id^="price-"] .price');
      const price = priceBox?.querySelector(priceBox.classList.contains('price--on-sale') ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular')?.textContent.trim() || '';
      this.setText(this.querySelector('[data-selection-summary]'), `${size}″ · ${finish}`);
      this.setText(this.querySelector('[data-sticky-selection]'), `${size}″ · ${finish}`);
      this.setText(this.querySelector('[data-sticky-price]'), price);
      this.setText(this.barButton, submit?.querySelector('span')?.textContent.trim() || (window.mlI18n || {}).chooseCanvas || 'Choose your canvas');
      const blocked = !submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true';
      const buttonPrice = submit?.querySelector('[data-submit-price]');
      this.setText(buttonPrice, price ? ` · ${price}` : '');
      if (buttonPrice) buttonPrice.hidden = blocked || !price;
      if (this.barButton && this.barButton.disabled !== blocked) this.barButton.disabled = blocked;
      if (this.bar) this.bar.hidden = !this.pastBuyButton;
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.visibility?.disconnect();
      this.product?.classList.remove('ml-pdp-enhanced');
    }
  });
}

/* Swapping the paired fulfillment products keeps size, gallery and cart in sync.
   Anchors remain fully functional without JavaScript. No third-party listing app. */
if (!window.mercyFinishNavigation) {
  window.mercyFinishNavigation = true;
  let pendingFinish;
  document.addEventListener('change', event => {
    if (event.target.closest('variant-selects')) pendingFinish?.abort();
  });
  document.addEventListener('click', async event => {
    const link = event.target.closest('.ml-linked-finishes a[data-ml-finish]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const info = link.closest('product-info');
    if (!info?.handleSwapProduct || typeof HTMLUpdateUtility === 'undefined') return;
    if (link.getAttribute('aria-current') === 'true') { event.preventDefault(); return; }
    event.preventDefault();
    pendingFinish?.abort();
    const controller = new AbortController(); pendingFinish = controller;
    // Cancel an older size request before starting a finish transition.
    info.abortController?.abort();
    info.resetProductFormState();
    const nav = link.closest('nav'); nav.setAttribute('aria-busy', 'true');
    const oldTop = nav.getBoundingClientRect().top;
    // Shop Pay's installments line renders async and sits at 0px for a moment
    // after every swap; hold its height so the content below doesn't jump.
    const currentPay = info.querySelector('.installment');
    const payHeight = currentPay?.offsetHeight || 0;
    if (currentPay && payHeight) currentPay.style.minHeight = `${payHeight}px`;
    const url = new URL(link.href);
    try {
      const response = await fetch(url, {signal: controller.signal});
      if (!response.ok) throw new Error('Product request failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = html.querySelector('product-info[id^="MainProduct"]');
      const selected = info.getSelectedVariant(incoming);
      if (!incoming || String(selected?.id) !== url.searchParams.get('variant')) throw new Error('Variant mismatch');
      if (controller.signal.aborted) return;
      const incomingPay = incoming.querySelector('.installment');
      if (incomingPay && payHeight) incomingPay.style.minHeight = `${payHeight}px`;
      const sameProduct = new URL(info.dataset.url, location.origin).pathname === url.pathname;
      if (sameProduct) {
        info.handleUpdateProductInfo(url.pathname)(html);
      } else {
        // Keep the current page visible while the incoming lead image decodes.
        const lead = incoming.querySelector('.product__media img');
        if (lead) {
          const preview = new Image();
          preview.srcset = lead.srcset; preview.sizes = lead.sizes; preview.src = lead.src;
          await Promise.race([preview.decode().catch(() => {}), new Promise(resolve => setTimeout(resolve, 1200))]);
        }
        if (controller.signal.aborted) return;
        // Dawn inserts the new <main> above the old one; scroll anchoring would then
        // throw the view ~4,700px down until the old <main> is removed (500ms later).
        const roots = [document.documentElement, document.body];
        roots.forEach(node => { node.style.overflowAnchor = 'none'; });
        info.handleSwapProduct(url.pathname, true)(html);
        setTimeout(() => roots.forEach(node => { node.style.overflowAnchor = ''; }), 700);
      }
      requestAnimationFrame(() => {
        const current = document.querySelector('main:not([hidden]) .ml-linked-finishes a[aria-current="true"]');
        if (current) {
          window.scrollBy(0, current.closest('nav').getBoundingClientRect().top - oldTop);
          current.focus({preventScroll: true});
        }
      });
    } catch (error) {
      if (error.name !== 'AbortError') window.location.assign(url.href);
    } finally { nav.removeAttribute('aria-busy'); }
  });
}

if (!customElements.get('mercy-product-film')) {
  customElements.define('mercy-product-film', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video'); this.button = this.querySelector('button');
      if (!this.video || !this.button) return;
      this.abort = new AbortController(); this.userPaused = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.video.muted = true;
      this.video.addEventListener('play', () => { this.button.textContent = (window.mlI18n || {}).pauseVideo || 'Pause video'; }, {signal:this.abort.signal});
      this.video.addEventListener('pause', () => { this.button.textContent = (window.mlI18n || {}).playVideo || 'Play video'; }, {signal:this.abort.signal});
      this.button.addEventListener('click', () => {
        this.userPaused = !this.video.paused;
        if (this.video.paused) this.video.play().catch(() => {}); else this.video.pause();
      }, {signal:this.abort.signal});
      this.observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && !this.userPaused) this.video.play().catch(() => {});
        else this.video.pause();
      }, {threshold:0.25});
      this.observer.observe(this);
    }
    disconnectedCallback() { this.observer?.disconnect(); this.abort?.abort(); this.video?.pause(); }
  });
}

/* Delivery dates from business-day estimates, counted in US Eastern time.
   Orders after noon ET (or on weekends) start the next business day. */
if (!customElements.get('ml-arrival')) {
  customElements.define('ml-arrival', class extends HTMLElement {
    connectedCallback() {
      const min = Number(this.dataset.min), max = Number(this.dataset.max);
      if (!min || !max) return;
      const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hourCycle: 'h23' }).formatToParts(new Date()).map(p => [p.type, p.value]));
      const start = new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day));
      const weekend = d => d.getUTCDay() === 0 || d.getUTCDay() === 6;
      if (+parts.hour >= 12 || weekend(start)) do { start.setUTCDate(start.getUTCDate() + 1); } while (weekend(start));
      const add = n => { const d = new Date(start); while (n > 0) { d.setUTCDate(d.getUTCDate() + 1); if (!weekend(d)) n--; } return d; };
      const fmt = (d, opts) => d.toLocaleDateString(document.documentElement.lang || 'en-US', { timeZone: 'UTC', ...opts });
      const first = add(min), last = add(max);
      const set = (selector, text) => { const el = this.querySelector(selector); if (el) el.textContent = text; };
      set('[data-arrival-label]', (window.mlI18n || {}).arrivesAsSoonAs || 'Arrives as soon as');
      set('[data-arrival-min]', fmt(first, { weekday: 'short', month: 'short', day: 'numeric' }));
      set('[data-arrival-from]', fmt(first, { month: 'short', day: 'numeric' }));
      set('[data-arrival-to]', fmt(last, { month: 'short', day: 'numeric' }));
      const range = this.querySelector('[data-arrival-range]');
      if (range) range.hidden = false;
    }
  });
}
